import { useMemo, useState, useEffect, useRef } from 'preact/hooks'
import { TypeAheadDropdown, type TypeAheadOption } from '@/components/common/TypeAheadDropdown'
import { getFieldLabelClasses, type LabelSize, type ControlDensity } from '@/components/common/formLabelUtils'
import { useTimeZones } from '@/components/calendar/hooks/useTimeZones'
import { BRAND_CLASSES } from '@/constants/theme'
import { buildTimeZoneChoices, filterTimeZoneChoices, type TimeZoneChoice } from '@/utils/timeZones'

interface TimeZoneComboboxProps {
  /** Bound IANA id, e.g. "Europe/London" (or '' / null / undefined when unset). */
  value: string | null | undefined
  /** Called with the selected IANA id ('' when cleared). */
  onChange: (ianaId: string) => void
  label?: string
  required?: boolean
  /** Field name / input id (defaults to "timeZone"). */
  name?: string
  error?: string
  /** Marks the field invalid when a form says why in a line of its own, which `describedBy` names. */
  invalid?: boolean
  describedBy?: string
  helperText?: string
  disabled?: boolean
  placeholder?: string
  /** Label sizing, matched to the surrounding form (defaults to 'sm'). */
  labelSize?: LabelSize
  density?: ControlDensity
}

/**
 * Searchable timezone selector (ORB-442). The user types a location — a city, region,
 * country, or abbreviation ("London", "New York", "England", "IST") — and picks a zone;
 * the bound value stays the IANA id the field already stores. Options are the curated
 * common zones (shown first, and on an empty search) plus every IANA zone the browser
 * knows, so a typed location reliably resolves to a real option. No backend call — this
 * builds on the existing `useTimeZones` lookup + the browser Intl database, and wraps the
 * shared `TypeAheadDropdown` primitive for keyboard nav / a11y / consistent styling.
 */
export function TimeZoneCombobox({
  value,
  onChange,
  label,
  required,
  name = 'timeZone',
  error,
  invalid,
  describedBy,
  helperText,
  disabled,
  placeholder = 'Type a city or timezone…',
  labelSize = 'sm',
  density = 'comfortable',
}: TimeZoneComboboxProps) {
  const { timeZones } = useTimeZones()

  // Build the option universe once per mount (offsets are DST-sensitive; a session is short).
  const choices = useMemo(() => buildTimeZoneChoices(timeZones, new Date()), [timeZones])
  const byId = useMemo(() => new Map(choices.map(c => [c.id, c])), [choices])

  const labelFor = (id: string | null | undefined): string => (id ? byId.get(id)?.label ?? id : '')

  const [search, setSearch] = useState(() => labelFor(value))

  // Re-sync the input text only when the committed value changes from outside (initial value,
  // form reset/cancel, cascade retry). We deliberately do NOT re-sync when the async lookup
  // lands (a byId change), which would otherwise clear a user's in-progress query mid-type.
  const lastSyncedValue = useRef(value)
  useEffect(() => {
    if (value !== lastSyncedValue.current) {
      lastSyncedValue.current = value
      setSearch(labelFor(value))
    }
  }, [value])

  const options: TypeAheadOption<TimeZoneChoice>[] = useMemo(
    () =>
      filterTimeZoneChoices(choices, search).map(c => ({
        key: c.id,
        label: c.label,
        data: c,
        render: () => (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-slate-900 dark:text-white">{c.label}</span>
            {c.detail && (
              <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">{c.detail}</span>
            )}
          </div>
        ),
      })),
    [choices, search]
  )

  return (
    <div>
      {label && (
        <label htmlFor={name} className={getFieldLabelClasses(density, labelSize)}>
          {label}
          {required && <span className={`${BRAND_CLASSES.INPUT.REQUIRED} ml-1`}>*</span>}
        </label>
      )}
      <TypeAheadDropdown<TimeZoneChoice>
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        invalid={invalid}
        describedBy={describedBy}
        minSearchLength={0}
        searchValue={search}
        onSearchChange={setSearch}
        options={options}
        onSelect={opt => {
          onChange(opt.data.id)
          setSearch(opt.data.label)
        }}
        onClear={() => onChange('')}
        noResultsMessage="No matching timezone"
      />
      {error && <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{error}</p>}
      {helperText && !error && <p className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{helperText}</p>}
    </div>
  )
}
